/* The mockup's `.sidebar`, `.sb-head`, `.brand`, `.sb-nav`, `.grp`, `.nav-item` and `.sb-foot`. */
.sidebar {
  display: flex;
  flex-direction: column;
  block-size: 100%;
  min-inline-size: 0;
  overflow: hidden;
  background: var(--surface-raised);
}

.head {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding: var(--space-3) var(--space-2) var(--space-2);
}

.brand {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  block-size: 32px;
  padding: 0 var(--space-2);
  font-size: var(--font-size-14);
  font-weight: var(--font-weight-semibold);
  white-space: nowrap;
}

.logo {
  display: grid;
  place-items: center;
  flex: none;
  inline-size: 24px;
  block-size: 24px;
  border-radius: var(--radius-control);
  background: var(--text);
  color: var(--surface-raised);
}

.nav {
  flex: 1;
  overflow-y: auto;
  padding: var(--space-1) var(--space-2) var(--space-2);
}

.group {
  margin-block-start: var(--space-3);
}

.groupLabel {
  display: flex;
  align-items: center;
  block-size: 24px;
  padding: 0 10px;
  color: var(--text-subtle);
  font-size: var(--font-size-12);
  font-weight: var(--font-weight-medium);
  white-space: nowrap;
}

.platform {
  margin-block-start: var(--space-4);
  padding-block-start: var(--space-3);
  border-top: 1px solid var(--border);
}

.badge {
  margin-inline-start: auto;
  padding: 0 6px;
  border: 1px solid var(--border);
  border-radius: var(--radius-pill);
  font-size: 11px;
  line-height: 16px;
}

.item {
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  inline-size: 100%;
  block-size: var(--control-height);
  padding: 0 10px;
  border: 0;
  border-radius: var(--radius-control);
  background: transparent;
  color: var(--text-muted);
  font-size: var(--font-size-13);
  font-weight: var(--font-weight-medium);
  text-align: start;
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
}

.item svg {
  flex: none;
  color: var(--text-subtle);
}

.item:hover {
  background: var(--state-hover);
  color: var(--text);
}

.item:focus-visible {
  outline: 2px solid var(--focus-ring);
  outline-offset: 2px;
}

.item[aria-current="page"] {
  background: var(--state-active);
  color: var(--text);
}

.item[aria-current="page"] svg {
  color: var(--action-text);
}

.item[aria-current="page"]::before {
  content: "";
  position: absolute;
  inset-block: 8px;
  inset-inline-start: -8px;
  inline-size: 2px;
  border-radius: 2px;
  background: var(--action-surface);
}

.foot {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: var(--space-2);
  border-top: 1px solid var(--border);
}

/* The rail (56px): icons only; every label stays the accessible name. */
.rail .label {
  position: absolute;
  inline-size: 1px;
  block-size: 1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}

.rail .groupLabel {
  display: none;
}

.rail .group {
  margin-block-start: var(--space-2);
  padding-block-start: var(--space-2);
  border-top: 1px solid var(--border);
}

.rail .group:first-child {
  border-top: 0;
}

.rail .brand,
.rail .item {
  justify-content: center;
  padding-inline: 0;
}

/* Shared by the switcher, the theme control and the user button. */
.control {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  inline-size: 100%;
  padding: 4px var(--space-2);
  border: 0;
  border-radius: var(--radius-control);
  background: transparent;
  color: var(--text);
  text-align: start;
  cursor: pointer;
}

.control:hover {
  background: var(--state-hover);
}

.control:focus-visible {
  outline: 2px solid var(--focus-ring);
  outline-offset: 2px;
}

.switcher {
  min-block-size: 40px;
  border: 1px solid var(--border);
  background: var(--surface-raised);
}

.switcher:hover {
  background: var(--surface-sunken);
}

.user {
  min-block-size: 44px;
}

.text {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-inline-size: 0;
  line-height: 1.25;
}

.text b,
.text small {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.text b {
  font-size: var(--font-size-13);
  font-weight: var(--font-weight-medium);
}

.text small {
  color: var(--text-subtle);
  font-size: var(--font-size-12);
}

.chevron {
  flex: none;
  color: var(--text-subtle);
}

.rail .control {
  justify-content: center;
  padding-inline: 0;
}

.rail .switcher {
  border-color: transparent;
}

.rail .text,
.rail .chevron {
  display: none;
}

/* Below 1024px the drawer rows are touch targets (the mockup's `body.mobile .nav-item`). */
.touch .item {
  block-size: 44px;
}

/* The account controls of the drawer are touch targets too (verifier minor 3): switcher, theme,
   user, and the usage meter, the link drawn straight in the foot. */
.touch .control,
.touch .foot > a {
  min-block-size: 44px;
}

/* The theme control reads as a nav item (the mockup's `.nav-item` in the foot). */
.navLike {
  gap: 10px;
  min-block-size: var(--control-height);
  padding: 0 10px;
  color: var(--text-muted);
  font-size: var(--font-size-13);
  font-weight: var(--font-weight-medium);
}

.navLike svg {
  color: var(--text-subtle);
}

.navLike:hover {
  color: var(--text);
}

/* The mockup's `.nav-count`: a number at the end of an item, hidden in the rail. */
.count {
  margin-inline-start: auto;
  color: var(--text-subtle);
  font-size: var(--font-size-12);
  font-variant-numeric: tabular-nums;
}

.rail .count {
  display: none;
}

.srOnly {
  position: absolute;
  inline-size: 1px;
  block-size: 1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}
